import { useState } from 'react';
import { GrStatusWarning } from 'react-icons/gr';
import { VscDebugRestart } from 'react-icons/vsc';
import { Dialog, Box, Typography, IconButton, Button, TextField, Stack, useTheme } from '@mui/material';
import Tooltip from '@/components/Common/Tooltip';

type Props = {
  businessName: string;
  businessId: number;
  disabled?: boolean;
  onReset?: any;
  tooltipTitle?: string;
};

export default function ResetBusinessButton({ businessName, businessId, disabled, onReset, tooltipTitle }: Props) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const [confirmName, setConfirmName] = useState('');
  const isMatch = confirmName === businessName;

  const handleReset = () => {
    onReset(businessId);
    setOpen(false);
    setConfirmName('');
  };

  return (
    <>
      <Tooltip title={tooltipTitle} placement='top'>
        <IconButton
          size='small'
          sx={{
            padding: { xs: '4px', sm: '8px' },
            '& svg': {
              fontSize: { xs: '18px', sm: '22px' }
            }
          }}
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          <VscDebugRestart />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '28px'
          }
        }}
      >
        <Box textAlign='center'>
          <Box
            sx={{
              width: 90,
              height: 90,
              borderRadius: '50%',
              backgroundColor: '#FFF4E5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto'
            }}
          >
            <GrStatusWarning color={theme.palette.warning.main} size={42} />
          </Box>
          <Typography fontSize={20} fontWeight={500} mb={1}>
            Reset Business & Start Billing
          </Typography>
          <Typography color='text.secondary' fontSize={14} mb={3}>
            You are about to reset <b>{businessName}</b>. This will clear test activity and prepare a clean workspace
            for handover. This action can not be undone.
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: '#F8F9FA',
            borderRadius: '4px',
            padding: '14px',
            fontSize: '14px',
            mb: 2
          }}
        >
          <Typography fontSize={14} fontWeight={500} mb={1}>
            What will be cleared:
          </Typography>

          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Call logs, worksheets, and recordings created during testing</li>
            <li>Configuration change history</li>
            <li>AI governance and compliance logs</li>
          </ul>
        </Box>
        <Box
          sx={{
            backgroundColor: '#FFFBEB',
            borderRadius: '4px',
            fontSize: '14px',
            padding: '14px',
            mb: 3
          }}
        >
          <Typography fontSize={14} sx={{ color: '#D97706' }}>
            <b>Billing Note:</b> Call minutes used during testing will be billed. Resetting only removes visible
            activity and prepares the account for the business to start fresh.
          </Typography>
        </Box>
        <Typography mb={1} fontSize={14} color='secondary'>
          Type "<b>{businessName}</b>" to confirm
        </Typography>

        <TextField
          fullWidth
          placeholder='Enter Business name'
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          sx={{ mb: 4 }}
        />
        <Stack direction='row' justifyContent='end' spacing={2}>
          <Button
            variant='outlined'
            onClick={() => setOpen(false)}
            sx={{
              borderRadius: '999px',
              paddingX: 4,
              paddingY: 1,
              borderColor: '#D1D5DB',
              color: '#555'
            }}
          >
            Cancel
          </Button>

          <Button
            variant='contained'
            disabled={!isMatch}
            onClick={handleReset}
            sx={{
              borderRadius: '999px',
              paddingX: 4,
              paddingY: 1,
              backgroundColor: '#D6A86E',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#c3955e',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                backgroundColor: '#D6A86E',
                color: '#fff',
                opacity: 0.5
              }
            }}
          >
            Reset Business
          </Button>
        </Stack>
      </Dialog>
    </>
  );
}
